import { useState, useEffect } from "react";
import API from "../lib/api";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ChartLineUp, Lightning, Users, Warning, ArrowClockwise } from "@phosphor-icons/react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, PieChart, Pie
} from "recharts";

const STATUS_COLORS = { "200": "#10B981", "201": "#10B981", "204": "#10B981", "400": "#F59E0B", "401": "#EF4444", "403": "#EF4444", "404": "#6B7280", "500": "#DC2626" };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border border-border rounded-md px-3 py-2 shadow-md text-xs">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i}>{p.name}: <span className="font-semibold">{p.value?.toLocaleString()}</span></p>
      ))}
    </div>
  );
};

export default function ApiUsagePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = () => {
    setLoading(true);
    API.get("/api-usage/overview")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load API usage"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetch(); }, []);

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading API usage data...</div>;
  if (!data) return <div className="text-center py-20 text-muted-foreground">No data available</div>;

  const { summary, top_tenants, hourly_chart, method_breakdown, status_breakdown, rate_limits } = data;

  const methodData = Object.entries(method_breakdown || {}).map(([name, value]) => ({ name, value }));
  const statusData = Object.entries(status_breakdown || {}).map(([name, value]) => ({ name: `${name}`, value }));

  return (
    <div data-testid="api-usage-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-cyan-600 to-blue-600 flex items-center justify-center">
            <ChartLineUp size={18} className="text-white" weight="fill" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">API Usage Dashboard</h1>
            <p className="text-xs text-muted-foreground">Monitor API requests, rate limits, and usage per tenant</p>
          </div>
        </div>
        <button onClick={fetch} className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1">
          <ArrowClockwise size={12} /> Refresh
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6" data-testid="api-usage-kpis">
        <div className="border border-border rounded-lg p-4 bg-card">
          <Lightning size={18} className="text-blue-500 mb-1" weight="duotone" />
          <p className="text-2xl font-bold font-[Chivo]">{summary.total_requests_24h?.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">Requests (24h)</p>
        </div>
        <div className="border border-border rounded-lg p-4 bg-card">
          <Users size={18} className="text-emerald-500 mb-1" weight="duotone" />
          <p className="text-2xl font-bold font-[Chivo]">{summary.active_tenants}</p>
          <p className="text-xs text-muted-foreground">Active Tenants</p>
        </div>
        <div className="border border-border rounded-lg p-4 bg-card">
          <Warning size={18} className="text-red-500 mb-1" weight="duotone" />
          <p className="text-2xl font-bold font-[Chivo]">{summary.rate_limit_violations}</p>
          <p className="text-xs text-muted-foreground">Rate Limit Violations</p>
        </div>
      </div>

      {/* Hourly Chart + Rate Limits */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 border border-border rounded-lg p-4 bg-card" data-testid="hourly-chart">
          <h3 className="text-sm font-semibold font-[Chivo] mb-3">Requests — Last 24 Hours</h3>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={hourly_chart}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="hour" tick={{ fontSize: 9 }} className="fill-muted-foreground" interval={2} />
              <YAxis tick={{ fontSize: 10 }} className="fill-muted-foreground" />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="requests" name="Requests" stroke="#3B82F6" fill="#3B82F660" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-border rounded-lg p-4 bg-card" data-testid="rate-limits-info">
          <h3 className="text-sm font-semibold font-[Chivo] mb-3">Rate Limits (req/min)</h3>
          <div className="space-y-2">
            {Object.entries(rate_limits).map(([plan, limit]) => (
              <div key={plan} className="flex items-center justify-between text-xs">
                <span className="capitalize">{plan.replace("_", " ")}</span>
                <Badge variant="outline" className="font-mono text-[10px]">{limit}/min</Badge>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Method + Status Breakdowns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="border border-border rounded-lg p-4 bg-card" data-testid="method-breakdown">
          <h3 className="text-sm font-semibold font-[Chivo] mb-3">Methods (24h)</h3>
          {methodData.length > 0 ? (
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={methodData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis type="number" tick={{ fontSize: 10 }} className="fill-muted-foreground" />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} className="fill-muted-foreground" width={50} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" name="Requests" fill="#8B5CF6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-xs text-muted-foreground text-center py-6">No data yet</p>}
        </div>

        <div className="border border-border rounded-lg p-4 bg-card" data-testid="status-breakdown">
          <h3 className="text-sm font-semibold font-[Chivo] mb-3">Status Codes (24h)</h3>
          {statusData.length > 0 ? (
            <div className="space-y-2">
              {statusData.sort((a, b) => b.value - a.value).map(s => (
                <div key={s.name} className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: STATUS_COLORS[s.name] || "#6B7280" }} />
                  <span className="text-xs font-mono w-8">{s.name}</span>
                  <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(5, (s.value / (statusData[0]?.value || 1)) * 100)}%`, background: STATUS_COLORS[s.name] || "#6B7280" }} />
                  </div>
                  <span className="text-[10px] text-muted-foreground w-12 text-right">{s.value.toLocaleString()}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-xs text-muted-foreground text-center py-6">No data yet</p>}
        </div>
      </div>

      {/* Top Tenants */}
      <div className="border border-border rounded-lg overflow-hidden" data-testid="top-tenants-table">
        <div className="px-4 py-3 bg-muted/30 border-b border-border">
          <h3 className="text-sm font-semibold font-[Chivo]">Top Tenants by API Usage (24h)</h3>
        </div>
        {top_tenants.length === 0 ? (
          <p className="px-4 py-8 text-xs text-muted-foreground text-center">No API usage recorded yet</p>
        ) : (
          <div className="divide-y divide-border">
            {top_tenants.map((t, i) => (
              <div key={t.tenant_id} className="flex items-center justify-between px-4 py-3" data-testid={`tenant-usage-${i}`}>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-5 text-right">{i + 1}.</span>
                  <div>
                    <p className="text-sm font-medium">{t.name}</p>
                    <p className="text-[10px] text-muted-foreground capitalize">{t.plan?.replace("_", " ")} plan</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-32 bg-muted rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-blue-500"
                      style={{ width: `${Math.min(100, (t.requests / (t.limit || 1)) * 100)}%` }}
                    />
                  </div>
                  <span className="text-xs font-mono">{t.requests.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
